import type { DriverActivity, Shift, Transport, TransportAssoc } from "@ots-frontend/ots-data";
import type { AuditObjectAuditSummary } from "@ots-frontend/ots-data";
import { getFileKind } from "../../components/OtsFileTypeIcon";
import { emptyTotals, TANK_DROPS_KEY, withTotals } from "./activity-entries";
import {
  demurrageFlag,
  mergeWarnings,
  operatorMismatchFlag,
  scadaFlag,
  ticketNumberFlag,
  validationSteps,
  warningFlag,
} from "./activity-flags";
import { stopMarkers, ticketMarkers } from "./activity-markers";
import type {
  ActivityAttachment,
  ActivityDriver,
  ActivityEntry,
  ActivityEntryKind,
  ActivityGeofenceMatch,
  ActivityHighlightOptions,
  ActivityShift,
} from "./activity-types";

/** Turns `/driver-activity/search` rows into drivers → shifts → entries. */
const sum = (...values: (number | undefined)[]) => {
  const present = values.filter((value): value is number => value != null);
  return present.length ? present.reduce((total, value) => total + value, 0) : undefined;
};

const geofenceMatch = (transport?: Transport): ActivityGeofenceMatch | undefined => transport?.geofenceEvent && {
  state: transport.skipGeofenceEvent ? "overridden" : "matched",
  event: transport.geofenceEvent,
};

const toAttachments = (transport: Transport | undefined, tenantCode?: string): ActivityAttachment[] =>
  (transport?.supplementals ?? []).map((file) => {
    const kind = file.supplementalGroup === "IMAGE" ? "image" : getFileKind(file.fileName ?? file.url);
    return compact({
      id: file.id,
      objectId: file.objectId ?? transport?.id,
      objectType: file.objectType ?? "TRANSPORT",
      name: file.description ?? file.fileName ?? (kind === "image" ? "Picture" : "Document"),
      kind,
      url: file.url,
      description: file.description,
      fileName: file.fileName,
      personName: file.personName,
      createDttm: file.createDttm,
      tenantCode,
    }) as ActivityAttachment;
  });

// WaterGo counts subcontractor charges apart from revenue, and whatever rate models are picked.
const subcontractorCost = (models: TransportAssoc["objectCostRateModelList"]) => {
  const total = (models ?? []).filter((model) => model.rateModelType === "Subcontractor" && (model.totalCost ?? 0) > 0)
    .reduce((sum, model) => sum + (model.totalCost ?? 0), 0);
  return total || undefined;
};

const blank = { markers: [], pickupMarkers: [], deliveryMarkers: [], attachments: [], pictures: [], documents: [], rateModels: [], isServiceTicket: false, warnings: [], flags: {}, validation: [], messages: [], durations: {}, needsAttention: false };

const compact = <T extends object>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;

const toEntry = (row: DriverActivity, options: BuildActivityOptions): ActivityEntry | undefined => {
  const tta = row.transportTransportAssoc;
  const pickup = tta?.pickupTransport ?? row.pickupTransport;
  const delivery = tta?.deliveryTransport ?? row.deliveryTransport;

  if (pickup || delivery) {
    const warnings = mergeWarnings(row.warnings, tta?.warnings);
    const kind: ActivityEntryKind = pickup && delivery ? "load" : pickup ? "pickup" : "delivery";
    const flags = compact<ActivityEntry["flags"]>({
      pickupTicket: pickup && ticketNumberFlag(pickup, warnings),
      deliveryTicket: delivery && ticketNumberFlag(delivery, warnings),
      pickupTime: warningFlag(warnings, "DRIVER_MISMATCH", "Driver mismatch."),
      pickupSite: operatorMismatchFlag(pickup, delivery),
      deliverySite: operatorMismatchFlag(pickup, delivery),
      // A validated quantity is locked, so SCADA no longer has a say.
      pickupQty: (tta?.qtyValidatedDttm != null ? undefined : scadaFlag(pickup, delivery, options))
        ?? warningFlag(warnings, "QUANTITY_MISMATCH", "Pickup and delivery quantities do not match."),
      deliveryQty: warningFlag(warnings, "QUANTITY_MISMATCH", "Pickup and delivery quantities do not match."),
      freight: warningFlag(warnings, "FREIGHT_TYPE_MISMATCH", "Pickup and delivery freight types do not match."),
      work: warningFlag(warnings, "SERVICE_TYPE_MISMATCH", "Pickup and delivery service types do not match."),
      revenue: warningFlag(warnings, "QUESTIONABLE_COST", "The calculated cost requires review.")
        ?? demurrageFlag(tta, options),
    });
    const pickupTime = tta?.pickupDttm ?? pickup?.createDttm;
    const deliveryTime = tta?.deliveryDttm ?? delivery?.createDttm;
    const selected = options.rateModelNames;
    const costs = tta?.objectCostRateModelList?.filter((model) => !selected || selected.has(model.rateModelName ?? ""));
    const flagged = Object.values(flags).some((flag) => flag.tone !== "success");
    const audit = tta && options.audits?.get(tta.id);
    const files = [...toAttachments(pickup, tta?.database), ...toAttachments(delivery, tta?.database)];
    const pictures = files.filter((file) => file.kind === "image");
    const documents = files.filter((file) => file.kind !== "image");

    const turnaround = tta?.turnAroundTime
      ?? (pickupTime != null && deliveryTime != null ? Math.max(deliveryTime - pickupTime, 0) : undefined);

    return {
      key: `${kind}_${tta?.id ?? pickup?.id ?? delivery?.id}`,
      kind,
      source: row,
      personId: pickup?.personId ?? delivery?.personId,
      time: pickupTime ?? deliveryTime,
      endTime: pickupTime != null ? deliveryTime : undefined,
      tta,
      pickup,
      delivery,
      category: tta?.category ?? pickup?.category ?? delivery?.category,
      resourceId: tta?.resourceIdentifiers?.join(", ") || pickup?.resourceId || delivery?.resourceId,
      audit,
      attachments: [...pictures, ...documents],
      pictures,
      documents,
      pickupGeofence: geofenceMatch(pickup),
      deliveryGeofence: geofenceMatch(delivery),
      isServiceTicket: pickup?.serviceTypeId != null || delivery?.serviceTypeId != null,
      markers: ticketMarkers(tta, pickup, delivery),
      pickupMarkers: [
        ...stopMarkers(pickup, tta?.pickupMultipartRecord),
        ...(tta?.qtyValidatedDttm != null ? [{ id: "qty-locked", label: "Quantity validated and locked", tone: "success" } as const] : []),
      ],
      deliveryMarkers: stopMarkers(delivery, tta?.deliveryMultipartRecord),
      warnings,
      flags,
      validation: validationSteps(tta),
      messages: [...(pickup?.messages ?? []), ...(delivery?.messages ?? [])],
      revenue: costs?.length ? costs.reduce((total, model) => total + Math.max(model.totalCost ?? 0, 0), 0) : undefined,
      cost: costs?.length ? costs.reduce((total, model) => total + (model.personCost ?? 0) + (model.trailerCost ?? 0), 0) : undefined,
      rateModels: costs ?? [],
      subcontractorCost: subcontractorCost(tta?.objectCostRateModelList),
      durationMillis: turnaround,
      durations: compact({
        turnaround,
        onSite: sum(pickup?.onSiteDuration, delivery?.onSiteDuration),
        travel: sum(pickup?.transitTime, delivery?.transitTime),
        billable: tta?.billableTime != null ? tta.billableTime * 3_600_000 : undefined,
      }),
      needsAttention: kind !== "load" || warnings.length > 0 || flagged || (audit?.blockingFindingCount ?? 0) > 0,
    };
  }

  if (row.geofenceEvent) {
    const event = row.geofenceEvent;
    return {
      key: `visit_${event.id}`,
      kind: "visit",
      source: row,
      personId: event.personId,
      ...blank,
      time: event.inDttm,
      endTime: event.outDttm,
      geofenceEvent: event,
      warnings: row.warnings ?? [],
      durationMillis: event.durationInMillis ?? (event.outDttm != null ? event.outDttm - event.inDttm : undefined),
    };
  }

  if (row.dispatch) {
    const dispatch = row.dispatch;
    return {
      key: `dispatch_${dispatch.id}`,
      kind: "dispatch",
      source: row,
      personId: dispatch.personId,
      ...blank,
      time: dispatch.scheduleDttm ?? dispatch.createDttm,
      dispatch,
      category: dispatch.category,
      markers: dispatch.pickupTransportId ? [{ id: "dispatch-link", label: "Picked up", tone: "success" }] : [],
      warnings: row.warnings ?? [],
    };
  }

  const work = row.serviceWorkStart ?? row.serviceWorkEnd;
  if (work) {
    return {
      key: `siteWork_${work.id}`,
      kind: "siteWork",
      source: row,
      personId: work.personId,
      ...blank,
      time: work.startDttm,
      endTime: work.endDttm,
      serviceWork: work,
      messages: work.messages ?? [],
      revenue: work.totalCost,
      durationMillis: work.endDttm != null ? Math.max(work.endDttm - work.startDttm, 0) : undefined,
    };
  }

  if (row.containerVolumeDrop) {
    const drop = row.containerVolumeDrop;
    return {
      key: `tankDrop_${drop.id}`,
      kind: "tankDrop",
      source: row,
      ...blank,
      time: drop.startDttm,
      endTime: drop.endDttm,
      containerVolumeDrop: drop,
      durationMillis: Math.max(drop.endDttm - drop.startDttm, 0),
      // A level drop with no ticket against it is the whole reason these rows exist.
      needsAttention: true,
    };
  }

  return undefined;
};

export type BuildActivityOptions = ActivityHighlightOptions & {
  /** Latest audit summaries by ticket id. */
  audits?: ReadonlyMap<string, AuditObjectAuditSummary>;
  /** "Now" for open shifts; injectable so totals are stable in tests and stories. */
  now?: number;
};

/** Groups the flat search result by driver and shift, keeping the server's order. */
export const buildDriverActivity = (rows: DriverActivity[] | undefined, options: BuildActivityOptions = {}): ActivityDriver[] => {
  const drivers = new Map<string, ActivityDriver>();

  const driverFor = (personId: string | undefined, name?: string, companyName?: string): ActivityDriver => {
    const key = personId ?? "unassigned";
    let driver = drivers.get(key);
    if (!driver) {
      driver = { key, personId, name: name ?? "Unassigned", companyName, shifts: [], totals: emptyTotals() };
      drivers.set(key, driver);
    } else {
      if (name && driver.name === "Unassigned") driver.name = name;
      driver.companyName ??= companyName;
    }
    return driver;
  };

  const shiftFor = (driver: ActivityDriver, shift: Shift): ActivityShift => {
    let group = driver.shifts.find((candidate) => candidate.shift?.id === shift.id);
    if (!group) {
      group = { key: `shift_${shift.id}`, shift, entries: [], totals: emptyTotals() };
      driver.shifts.push(group);
    }
    return group;
  };

  for (const row of rows ?? []) {
    if (row.person) {
      const { id, firstName, lastName, companyName } = row.person;
      driverFor(id, [firstName, lastName].filter(Boolean).join(" "), companyName);
      continue;
    }

    const shift = row.startShift ?? row.endShift;
    if (shift) {
      shiftFor(driverFor(shift.personId, shift.personName, shift.personCompanyName), shift);
      continue;
    }

    // Tickets raised inside site work (`serviceWorkPickupTransport` / `serviceWorkDeliveryTransport`) are deliberately left out.
    const entry = toEntry(row, options);
    if (!entry) continue;

    if (entry.kind === "tankDrop") {
      const drops = driverFor(TANK_DROPS_KEY, "Unticketed tank drops");
      if (!drops.shifts.length) drops.shifts.push({ key: `${TANK_DROPS_KEY}_no_shift`, entries: [], totals: emptyTotals() });
      drops.shifts[0].entries.push(entry);
      continue;
    }

    const transport = entry.pickup ?? entry.delivery;
    const driver = driverFor(
      entry.personId,
      transport?.personName ?? entry.geofenceEvent?.personName ?? entry.dispatch?.personName ?? entry.serviceWork?.personName
    );
    if (entry.kind === "siteWork") {
      // Start and end arrive as two rows of the same site work; the end row carries the finished record.
      const existing = driver.shifts.flatMap((candidate) => candidate.entries).find((candidate) => candidate.key === entry.key);
      if (existing) {
        Object.assign(existing, entry);
        continue;
      }
    }
    const shiftId = transport?.shiftId ?? entry.geofenceEvent?.shiftId;
    // Activity belongs to the shift it names, otherwise to the one it was listed under.
    let group = driver.shifts.find((candidate) => shiftId != null && candidate.shift?.id === shiftId)
      ?? driver.shifts[driver.shifts.length - 1];
    if (!group) {
      group = { key: `${driver.key}_no_shift`, entries: [], totals: emptyTotals() };
      driver.shifts.push(group);
    }
    group.entries.push(entry);
    entry.shift = group.shift;
    markGeofenceOutsideShift(entry, group.shift);
  }

  const now = options.now ?? Date.now();
  return [...drivers.values()].map((driver) => withTotals(driver, driver.shifts, now));
};

const minute = (value: number) => Math.floor(value / 60_000);

/** The legacy check: a stop whose geofence visit ran past the end of the shift it is listed under. */
const markGeofenceOutsideShift = (entry: ActivityEntry, shift: Shift | undefined) => {
  if (shift?.endDttm == null) return;
  const end = shift.endDttm;
  for (const match of [entry.pickupGeofence, entry.deliveryGeofence]) {
    const out = match?.event.outDttm;
    if (match && out != null && minute(out) > minute(end)) match.state = "outside-shift";
  }
};
